import Vue from "vue";
import store from "@/store/index";
import VueRouter from "vue-router";

// Auth views
import Login from "@/views/auth/Login.vue";
import Register from "@/views/auth/Register.vue";
import ForgotPassword from "@/views/auth/ForgotPassword.vue";
import ResetPassword from "@/views/auth/ResetPassword.vue";
import VerifyAccount from "@/views/auth/VerifyAccount.vue";

// Page views
import Dashboard from "@/views/pages/Dashboard.vue";
import Explore from "@/views/pages/Explore.vue";
import Timeline from "@/views/pages/Timeline.vue";
import Following from "@/views/pages/Following.vue";
import Profile from "@/views/pages/Profile.vue";
import Settings from "@/views/pages/Settings.vue";

Vue.use(VueRouter);

export const ViewName = {
  Start: "Start",
  UserLogin: "Login",
  UserRegister: "Register",
  ForgotPassword: "ForgotPassword",
  ResetPassword: "ResetPassword",
  VerifyAccount: "VerifyAccount",
  Dashboard: "Dashboard",
  Explore: "Explore",
  Timeline: "Timeline",
  Following: "Following",
  Profile: "Profile",
  Settings: "Settings",
};

// `meta.public` marks routes a signed-out visitor is allowed to sit on.
// Without it App.vue sends anyone without a session back to the start page,
// which would bounce people straight off their own emailed reset link.
export const routes = [
  {
    path: "/",
    name: ViewName.Start,
    component: Login,
    meta: { public: true },
  },
  {
    path: "/login",
    name: ViewName.UserLogin,
    component: Login,
    meta: { public: true },
  },
  {
    path: "/register",
    name: ViewName.UserRegister,
    component: Register,
    meta: { public: true },
  },
  {
    path: "/forgot-password",
    name: ViewName.ForgotPassword,
    component: ForgotPassword,
    meta: { public: true },
  },
  // Both links are mailed as /{hashedEmail}/{userId}/{token}.
  {
    path: "/reset-password/:hash/:key/:token",
    name: ViewName.ResetPassword,
    component: ResetPassword,
    meta: { public: true },
  },
  {
    path: "/verify/:hash/:key/:token",
    name: ViewName.VerifyAccount,
    component: VerifyAccount,
    meta: { public: true },
  },
  {
    path: "/dashboard",
    name: ViewName.Dashboard,
    component: Dashboard,
    meta: { requiresAuth: true },
  },
  {
    path: "/explore",
    name: ViewName.Explore,
    component: Explore,
    meta: { requiresAuth: true },
  },
  {
    path: "/timeline",
    name: ViewName.Timeline,
    component: Timeline,
    meta: { requiresAuth: true },
  },
  {
    path: "/following",
    name: ViewName.Following,
    component: Following,
    meta: { requiresAuth: true },
  },
  {
    path: "/profile",
    name: ViewName.Profile,
    component: Profile,
    meta: { requiresAuth: true },
  },
  {
    path: "/settings",
    name: ViewName.Settings,
    component: Settings,
    meta: { requiresAuth: true },
  },
  // Catch-all: unknown paths fall back to the start page instead of a blank
  // router-view (which, on a hard load, surfaces as a 404).
  {
    path: "*",
    redirect: "/",
  },
];

const router = new VueRouter({
  linkActiveClass: "route-active",
  mode: "history",
  base: process.env.BASE_URL,
  routes,
});

// Redirect to login if not authenticated.
router.beforeEach((to, from, next) => {
  const loggedIn = store.getters.isLoggedIn;
  const isLoginPage =
    to.name === ViewName.Start || to.name === ViewName.UserLogin;

  if (to.matched.some((record) => record.meta.requiresAuth) && !loggedIn) {
    // Protected route without a session: send to the start/login page.
    next({ name: ViewName.Start, query: { redirect: to.fullPath } });
  } else if (isLoginPage && loggedIn) {
    // Already authenticated but landing on a login page: go to the dashboard.
    next({ name: ViewName.Dashboard });
  } else {
    next();
  }
});

export default router;
